import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Library, Play, Pause, Loader2, Square, Volume2, Trash2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";
import { ArtThumb } from "@/components/riptag/TrackTable";

export const LibraryTab = ({ onExport }) => {
  const [q, setQ] = useState("");
  const [tracks, setTracks] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ decades: [], genres: [] });
  const [fDecade, setFDecade] = useState("");
  const [fGenre, setFGenre] = useState("");
  const [fRotation, setFRotation] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [preview, setPreview] = useState({ id: "", state: "idle" });
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [bulkRotation, setBulkRotation] = useState("heavy");
  const audioRef = useRef(null);
  const urlCache = useRef({});

  const load = useCallback(async (query, filters = {}) => {
    try {
      const r = await api.get("/rip/library", { params: { q: query || "", ...filters } });
      setTracks(r.data.tracks || []);
      setTotal(r.data.total || 0);
      setFacets(r.data.facets || { decades: [], genres: [] });
      setSelected(new Set());
    } catch { /* empty */ }
  }, []);

  const filters = { decade: fDecade, genre: fGenre, rotation: fRotation };

  useEffect(() => { load(q, { decade: fDecade, genre: fGenre, rotation: fRotation }); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fDecade, fGenre, fRotation]);

  useEffect(() => { load(""); }, [load]);

  useEffect(() => () => {
    audioRef.current?.pause();
    Object.values(urlCache.current).forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const stopPreview = () => {
    audioRef.current?.pause();
    setPreview({ id: "", state: "idle" });
    setProgress(0);
  };

  const togglePreview = async (t) => {
    const audio = audioRef.current || (audioRef.current = new Audio());
    if (preview.id === t.track_id && preview.state !== "loading") {
      if (preview.state === "playing") {
        audio.pause();
        setPreview({ id: t.track_id, state: "paused" });
      } else {
        audio.play();
        setPreview({ id: t.track_id, state: "playing" });
      }
      return;
    }
    audio.pause();
    setProgress(0);
    setPreview({ id: t.track_id, state: "loading" });
    try {
      let url = urlCache.current[t.track_id];
      if (!url) {
        const r = await api.get(`/rip/tracks/${t.track_id}/download`, { responseType: "blob" });
        url = URL.createObjectURL(r.data);
        urlCache.current[t.track_id] = url;
      }
      audio.src = url;
      audio.onended = stopPreview;
      audio.ontimeupdate = () => setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
      await audio.play();
      setPreview({ id: t.track_id, state: "playing" });
    } catch {
      toast.error("Couldn't load audio preview");
      setPreview({ id: "", state: "idle" });
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/rip/library/suggest-rotation");
      setSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) toast.info("No unassigned tracks with a year — nothing to suggest");
    } catch { toast.error("Couldn't build suggestions"); }
    setSuggesting(false);
  };

  const applySuggestions = async (level) => {
    const ids = (suggestions || []).filter((s) => s.suggestion === level).map((s) => s.track_id);
    if (!ids.length) return;
    try {
      const r = await api.post("/rip/library/bulk", { track_ids: ids, action: "rotation", rotation: level });
      toast.success(`${r.data.affected} tracks set to ${level} rotation`);
      setSuggestions((s) => s.filter((x) => x.suggestion !== level));
      load(q, filters);
    } catch { toast.error("Apply failed"); }
  };

  const nowPlaying = tracks.find((t) => t.track_id === preview.id);

  const deleteTrack = async (t) => {
    if (!(await appConfirm(`Delete "${t.title}" from your library permanently? The audio file will also be removed.`))) return;
    if (preview.id === t.track_id) stopPreview();
    try {
      await api.delete(`/rip/tracks/${t.track_id}`);
      toast.success("Track deleted");
      load(q, filters);
    } catch { toast.error("Delete failed"); }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    setSelected((s) => s.size === tracks.length ? new Set() : new Set(tracks.map((t) => t.track_id)));
  };

  const bulkDelete = async () => {
    if (!(await appConfirm(`Delete the ${selected.size} selected tracks permanently? Their audio files will also be removed.`))) return;
    if (selected.has(preview.id)) stopPreview();
    try {
      const r = await api.post("/rip/library/bulk", { track_ids: [...selected], action: "delete" });
      toast.success(`${r.data.affected} tracks deleted`);
      load(q, filters);
    } catch { toast.error("Bulk delete failed"); }
  };

  const bulkRotate = async () => {
    try {
      const r = await api.post("/rip/library/bulk", { track_ids: [...selected], action: "rotation", rotation: bulkRotation });
      toast.success(`Rotation set to ${bulkRotation} on ${r.data.affected} tracks`);
      load(q, filters);
    } catch { toast.error("Bulk update failed"); }
  };

  const clearLibrary = async () => {
    if (!(await appConfirm(
      `This permanently deletes all ${total} library tracks AND their audio files from storage. There is no undo.`,
      { title: "Clear entire library?", confirmText: "Delete everything" }))) return;
    stopPreview();
    try {
      const r = await api.delete("/rip/library");
      toast.success(`Library cleared — ${r.data.removed} tracks removed`);
      load(q, filters);
    } catch { toast.error("Clear failed"); }
  };

  return (
    <div className="space-y-4" data-testid="rip-library-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
          <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value, filters); }}
            placeholder="Search title, artist, album…" data-testid="rip-library-search"
            className="w-full rounded-md border border-line bg-bg-elevated pl-8 pr-3 py-2 text-sm focus:border-brand-amber focus:outline-none" />
        </div>
        <button onClick={() => onExport("m3u8", "library")} data-testid="rip-library-export-m3u8"
          className="rounded-full border border-line px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-ink-secondary hover:text-ink-primary transition">
          Export M3U8
        </button>
        <button onClick={() => onExport("csv", "library")} data-testid="rip-library-export-csv"
          className="rounded-full border border-line px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-ink-secondary hover:text-ink-primary transition">
          Export CSV
        </button>
        {total > 0 && (
          <button onClick={clearLibrary} data-testid="rip-library-clear-btn"
            className="rounded-full border border-red-500/40 px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-red-400 hover:bg-red-500/10 hover:border-red-500/70 transition">
            Clear Library
          </button>
        )}
      </div>

      <div className="text-xs font-mono text-ink-muted">{total} tracks in library</div>

      <div className="flex flex-wrap items-center gap-2" data-testid="rip-library-filters">
        <select value={fDecade} onChange={(e) => setFDecade(e.target.value)} data-testid="rip-filter-decade"
          className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs focus:border-brand-amber focus:outline-none">
          <option value="">All decades</option>
          {facets.decades.map((d) => <option key={d.decade} value={d.decade}>{d.decade} ({d.n})</option>)}
        </select>
        <select value={fGenre} onChange={(e) => setFGenre(e.target.value)} data-testid="rip-filter-genre"
          className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs focus:border-brand-amber focus:outline-none max-w-[180px]">
          <option value="">All genres</option>
          {facets.genres.map((g) => <option key={g.genre} value={g.genre}>{g.genre} ({g.n})</option>)}
        </select>
        <select value={fRotation} onChange={(e) => setFRotation(e.target.value)} data-testid="rip-filter-rotation"
          className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs capitalize focus:border-brand-amber focus:outline-none">
          <option value="">All rotations</option>
          {["heavy", "medium", "light", "unassigned"].map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {(fDecade || fGenre || fRotation) && (
          <button onClick={() => { setFDecade(""); setFGenre(""); setFRotation(""); }} data-testid="rip-filter-clear"
            className="text-xs text-ink-muted underline hover:text-ink-primary">Clear filters</button>
        )}
        <button onClick={suggest} disabled={suggesting} data-testid="rip-suggest-rotation"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-brand-cyan/50 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-brand-cyan hover:bg-brand-cyan/10 transition disabled:opacity-50">
          {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Suggest rotation
        </button>
      </div>

      {suggestions && suggestions.length > 0 && (
        <div className="rounded-xl border border-brand-cyan/40 bg-bg-surface px-4 py-3" data-testid="rip-suggestions-panel">
          <div className="text-xs font-mono uppercase tracking-wider text-brand-cyan mb-2">
            Rotation suggestions — for manual review ({suggestions.length} tracks)
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {["heavy", "medium", "light"].map((lvl) => {
              const n = suggestions.filter((s) => s.suggestion === lvl).length;
              if (!n) return null;
              return (
                <button key={lvl} onClick={() => applySuggestions(lvl)} data-testid={`rip-apply-suggest-${lvl}`}
                  className="rounded-full border border-brand-cyan/50 px-3 py-1 text-xs text-brand-cyan hover:bg-brand-cyan/10 transition capitalize">
                  Apply {lvl} to {n} track{n > 1 ? "s" : ""}
                </button>
              );
            })}
            <button onClick={() => setSuggestions(null)} className="ml-auto text-xs text-ink-muted hover:text-ink-primary" data-testid="rip-suggest-dismiss">Dismiss</button>
          </div>
          <div className="space-y-0.5 max-h-36 overflow-y-auto">
            {suggestions.slice(0, 50).map((s) => (
              <div key={s.track_id} className="text-[11px] font-mono text-ink-secondary truncate">
                <span className={`uppercase font-bold mr-1.5 ${s.suggestion === "heavy" ? "text-brand-amber" : s.suggestion === "medium" ? "text-brand-cyan" : "text-ink-muted"}`}>{s.suggestion}</span>
                {s.artist} - {s.title} <span className="text-ink-muted">· {s.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-amber/40 bg-bg-surface px-4 py-2.5" data-testid="rip-bulk-bar">
          <span className="text-xs font-mono uppercase tracking-wider text-brand-amber">{selected.size} selected</span>
          <div className="flex items-center gap-1.5">
            <select value={bulkRotation} onChange={(e) => setBulkRotation(e.target.value)} data-testid="rip-bulk-rotation-select"
              className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs capitalize focus:border-brand-amber focus:outline-none">
              {["heavy", "medium", "light", "unassigned"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={bulkRotate} data-testid="rip-bulk-rotation-apply"
              className="rounded-full border border-brand-amber/50 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-brand-amber hover:bg-brand-amber/10 transition">
              Set rotation
            </button>
          </div>
          <button onClick={bulkDelete} data-testid="rip-bulk-delete-btn"
            className="rounded-full border border-red-500/40 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-red-400 hover:bg-red-500/10 transition">
            Delete selected
          </button>
          <button onClick={() => setSelected(new Set())} data-testid="rip-bulk-clear-selection"
            className="ml-auto text-xs text-ink-muted hover:text-ink-primary transition">
            Clear selection
          </button>
        </div>
      )}

      {nowPlaying && (
        <div className="flex items-center gap-3 rounded-xl border border-brand-amber/40 bg-bg-surface px-4 py-2.5" data-testid="rip-preview-bar">
          <Volume2 className="h-4 w-4 text-brand-amber shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{nowPlaying.title}</div>
            <div className="text-[10px] font-mono text-ink-muted truncate">
              {nowPlaying.artist}{nowPlaying.variant && nowPlaying.variant !== "original" ? ` · ${nowPlaying.variant.replace("_", " ").toUpperCase()}` : ""}
            </div>
          </div>
          <div className="flex-1 h-1.5 rounded-full bg-bg-elevated overflow-hidden">
            <div className="h-full bg-brand-amber transition-[width] duration-300" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <button onClick={() => togglePreview(nowPlaying)} data-testid="rip-preview-bar-toggle"
            className="shrink-0 text-ink-secondary hover:text-brand-amber transition">
            {preview.state === "playing" ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button onClick={stopPreview} data-testid="rip-preview-stop"
            className="shrink-0 text-ink-muted hover:text-red-400 transition">
            <Square className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {!tracks.length ? (
        <div className="rounded-xl border border-line bg-bg-surface p-10 text-center text-sm text-ink-muted">
          <Library className="h-6 w-6 mx-auto mb-2 opacity-50" />
          Nothing here yet — add tracks from a rip session.
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                <th className="pl-4 pr-1 py-2.5 w-8">
                  <input type="checkbox" checked={tracks.length > 0 && selected.size === tracks.length}
                    onChange={toggleSelectAll} data-testid="rip-select-all"
                    className="accent-[#FF8A00] h-3.5 w-3.5 cursor-pointer align-middle" />
                </th>
                <th className="px-4 py-2.5">Title</th>
                <th className="px-4 py-2.5">Artist</th>
                <th className="px-4 py-2.5 hidden md:table-cell">Album</th>
                <th className="px-4 py-2.5 hidden lg:table-cell">Genre</th>
                <th className="px-4 py-2.5">Variant</th>
                <th className="px-4 py-2.5">Rotation</th>
                <th className="px-2 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {tracks.map((t) => {
                const isActive = preview.id === t.track_id;
                return (
                <tr key={t.track_id} data-testid={`rip-lib-row-${t.track_id}`} className={isActive || selected.has(t.track_id) ? "bg-bg-elevated" : ""}>
                  <td className="pl-4 pr-1 py-2.5">
                    <input type="checkbox" checked={selected.has(t.track_id)}
                      onChange={() => toggleSelect(t.track_id)} data-testid={`rip-select-${t.track_id}`}
                      className="accent-[#FF8A00] h-3.5 w-3.5 cursor-pointer align-middle" />
                  </td>
                  <td className="px-4 py-2.5 font-semibold">
                    <span className="flex items-center gap-2.5">
                      <button onClick={() => togglePreview(t)} data-testid={`rip-preview-btn-${t.track_id}`}
                        title={isActive && preview.state === "playing" ? "Pause preview" : "Play preview"}
                        className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-full border transition ${
                          isActive ? "border-brand-amber text-brand-amber bg-brand-amber/10"
                                   : "border-line text-ink-muted hover:text-brand-amber hover:border-brand-amber/60"}`}>
                        {isActive && preview.state === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : isActive && preview.state === "playing" ? <Pause className="h-3.5 w-3.5" />
                          : <Play className="h-3.5 w-3.5 ml-0.5" />}
                      </button>
                      <ArtThumb url={t.art_url} size="w-8 h-8" testId={`rip-lib-art-${t.track_id}`} />
                      <span className="truncate">{t.title}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-ink-secondary">{t.artist}</td>
                  <td className="px-4 py-2.5 text-ink-muted hidden md:table-cell">{t.album}</td>
                  <td className="px-4 py-2.5 text-ink-muted hidden lg:table-cell">{t.genre}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase border ${
                      t.variant === "explicit" ? "border-red-500/50 text-red-300" :
                      t.variant === "clean" ? "border-emerald-500/50 text-emerald-300" :
                      "border-line text-ink-muted"}`}>
                      {(t.variant || "original").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-ink-secondary capitalize">{t.rotation || "unassigned"}</td>
                  <td className="px-2 py-2.5">
                    <button onClick={() => deleteTrack(t)} data-testid={`rip-lib-delete-${t.track_id}`}
                      title="Delete track" className="text-ink-muted hover:text-red-400 transition p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
